import { useState, useEffect } from 'react';
import {
  LEGACY_UNBRANDED_FORM_COLOR,
  brandPaletteFor,
  isUnbrandedFormColor,
  isUnbrandedPalette,
} from './brandPalette';
import { UNBRANDED_THEME } from './unbrandedTheme';

// Legacy color mapping for backward compatibility
export const LEGACY_COLOR_MAPPING = {
  lightColor: 'accent-50',    // primaryColor equivalent
  darkColor: 'accent-60',     // darkColor equivalent
  primaryColor: 'accent-50'   // Direct mapping
};

// Enhanced brand color hook with legacy support
export const useBrandColors = (baseColor, legacyColors = {}) => {
  const [brandColors, setBrandColors] = useState({});
  // The first render, before the effect below fills the palette, already
  // paints the neutral pair for the legacy "no colour" placeholder, so an
  // unbranded widget never shows the placeholder for a frame (CON-331).
  const unbranded = isUnbrandedFormColor(baseColor);
  // CON-528: no colour at all paints the same neutral pair, never a stand-in sage.
  const [primaryColor, setPrimaryColor] = useState(
    unbranded || !baseColor ? UNBRANDED_THEME.primaryColor : baseColor,
  );
  const [darkColor, setDarkColor] = useState(
    unbranded ? UNBRANDED_THEME.darkColor : legacyColors.darkColor || UNBRANDED_THEME.darkColor,
  );

  useEffect(() => {
    if (baseColor) {
      // The legacy "no colour" placeholder resolves to the neutral donor set;
      // every other colour is `generateBrandColors`, unchanged (CON-331).
      const generated = brandPaletteFor(baseColor);
      setBrandColors(generated);
      // Define standardized color variables
      setPrimaryColor(generated['accent-50']);
      setDarkColor(generated['accent-60']);
    }
  }, [baseColor]);

  return {
    brandColors,
    primaryColor,  // Now equals brandColors['accent-50']
    darkColor,     // Now equals brandColors['accent-60']
    // Legacy getters for backward compatibility
    lightColor: primaryColor,
    buttonPressColor: darkColor
  };
};

// Utility to extract colors for PostMessage.
//
// The neutral palette an organisation with no colour renders with (CON-331)
// sends NO palette and no pressed colour, so nothing blue is on the wire. Its
// base stays the stored placeholder, exactly the string it always sent: the
// checkout reads that as "unbranded" and paints its own neutral route
// (`brandPayloadFor`, CON-115's route), and every charge payload keeps sending
// the colour it always sent, so the money path's wire does not move.
export const prepareColorsForPostMessage = (brandColors) => {
  if (!brandColors || Object.keys(brandColors).length === 0) {
    return {
      brandColors: null,
      primaryColor: null,
      darkColor: null,
      lightColor: null,
      buttonPressColor: null
    };
  }

  if (isUnbrandedPalette(brandColors)) {
    return {
      brandColors: null,
      primaryColor: LEGACY_UNBRANDED_FORM_COLOR,
      darkColor: null,
      lightColor: LEGACY_UNBRANDED_FORM_COLOR,
      buttonPressColor: null
    };
  }

  return {
    brandColors,
    primaryColor: brandColors['accent-50'],
    darkColor: brandColors['accent-60'],
    lightColor: brandColors['accent-50'],      // Legacy compatibility
    buttonPressColor: brandColors['accent-60'] // Legacy compatibility
  };
};
